import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
  Tabs,
  Tab,
  Box,
  Alert,
  Snackbar,
  Paper
} from '@mui/material';
import { Search, UserPlus, X, Users, Share2 } from 'lucide-react';
import { 
  ENTITY_TYPES, 
  getAccessList, 
  addAccess, 
  removeAccess,
} from '../../services/shareService';
import { usersApi } from '../../services/api';
import debounce from 'lodash/debounce';

/**
 * Unified ShareDialog component for sharing entities (sandboxes, templates, tutors)
 * with users and groups
 */
const ShareDialog = ({ 
  open, 
  onClose, 
  entity, 
  entityType, 
  token, 
  onSuccess,
  onError
}) => {
  const { t } = useTranslation();

  // State variables
  const [tabValue, setTabValue] = useState(0); // 0 for users, 1 for groups
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [accessList, setAccessList] = useState({ users: [], groups: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Get entity name based on entity type
  const getEntityName = () => {
    if (!entity) return '';
    return entity.name || entity.title || '';
  };

  // Get translation keys based on entity type
  const getTranslationKey = (key) => {
    // For title and description, use entity-specific translations
    if (key === 'title' || key === 'description') {
      switch (entityType) {
        case ENTITY_TYPES.SANDBOX:
          // For sandbox, use share_title and share_description
          return `dashboard.sandboxes.${key === 'title' ? 'share_title' : 'share_description'}`;
        case ENTITY_TYPES.TEMPLATE:
          // For template, use share_title and share_description
          return `dashboard.templates.${key === 'title' ? 'share_title' : 'share_description'}`;
        case ENTITY_TYPES.AI_TUTOR:
          // For AI tutor, use the AI tutor share dialog translations
          return `dashboard.ai_tutors.share_dialog.${key}`;
        default:
          // For other entity types, use common share dialog translations
          return `common.share_dialog.${key}`;
      }
    }

    // For all other keys, use common share dialog translations
    if (key === 'share_with_users' || 
        key === 'share_with_groups' || 
        key === 'search_users' || 
        key === 'search_groups' || 
        key === 'selected_users' || 
        key === 'selected_groups' || 
        key === 'loading_access' || 
        key === 'users_with_access' || 
        key === 'groups_with_access' || 
        key === 'no_users' || 
        key === 'no_groups' || 
        key === 'cancel' || 
        key === 'share' || 
        key === 'access_removed' || 
        key === 'sharing_success' || 
        key.startsWith('error.')) {
      return `common.share_dialog.${key}`;
    }

    // Use entity-specific translations for entity-specific functionality
    switch (entityType) {
      case ENTITY_TYPES.SANDBOX:
        return `dashboard.sandboxes.${key}`;
      case ENTITY_TYPES.TEMPLATE:
        return `dashboard.templates.${key}`;
      case ENTITY_TYPES.AI_TUTOR:
        return `dashboard.ai_tutors.share_dialog.${key}`;
      default:
        return `dashboard.share_dialog.${key}`;
    }
  };

  // Load current access list when dialog opens
  useEffect(() => {
    if (open && entity) {
      loadAccessList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity]);

  // Load entity access list
  const loadAccessList = async () => {
    if (!entity || !entity.id) return;

    setIsLoading(true);
    try {
      const accessData = await getAccessList(token, entityType, entity.id);

      // Separate users and groups
      const users = accessData.filter(item => item.user !== null);
      const groups = accessData.filter(item => item.group !== null);

      setAccessList({ users, groups });
    } catch (err) {
      const errorMsg = t(getTranslationKey('error.loading_access'));
      setErrorMessage(errorMsg);
      if (onError) onError(errorMsg);
      console.error(`Error loading ${entityType} access list:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback((term) => {
    if (!term.trim()) return;

    setIsSearching(true);
    const searchPromise = async () => {
      try {
        let results;
        if (tabValue === 0) {
          // Search for users
          results = await usersApi.searchUsers(term, token);
        } else {
          // Search for groups
          results = await usersApi.searchGroups(term, token);
        }
        setSearchResults(results);
      } catch (err) {
        const errorKey = tabValue === 0
          ? getTranslationKey('error.user_search_error')
          : getTranslationKey('error.group_search_error');
        const errorMsg = t(errorKey);
        setErrorMessage(errorMsg);
        if (onError) onError(errorMsg);
        console.error('Error searching:', err);
      } finally {
        setIsSearching(false);
      }
    };

    const debouncedFn = debounce(() => searchPromise(), 300);
    debouncedFn();

    return () => debouncedFn.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, token, t, onError, setIsSearching, setSearchResults, setErrorMessage]);

  // Handle search term change
  useEffect(() => {
    let cancelSearch;
    if (searchTerm) {
      cancelSearch = debouncedSearch(searchTerm);
    }
    return () => {
      if (cancelSearch) {
        cancelSearch();
      }
    };
  }, [searchTerm, debouncedSearch]);

  // Handle tab change (users/groups)
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Add user or group to selection
  const handleSelectItem = (item) => {
    if (tabValue === 0) {
      // Add user if not already selected
      if (!selectedUsers.find(u => u.id === item.id)) {
        setSelectedUsers([...selectedUsers, item]);
      }
    } else {
      // Add group if not already selected
      if (!selectedGroups.find(g => g.id === item.id)) {
        setSelectedGroups([...selectedGroups, item]);
      }
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  // Remove user from selection
  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
  };

  // Remove group from selection
  const handleRemoveGroup = (groupId) => {
    setSelectedGroups(selectedGroups.filter(group => group.id !== groupId));
  };

  // Remove user or group access
  const handleRemoveAccess = async (accessId) => {
    try {
      await removeAccess(token, entityType, accessId);
      await loadAccessList(); // Refresh the access list
      setSuccessMessage(t(getTranslationKey('access_removed')));
    } catch (err) {
      const errorMsg = t(getTranslationKey('error.removing_access'));
      setErrorMessage(errorMsg);
      if (onError) onError(errorMsg);
      console.error('Error removing access:', err);
    }
  };

  // Save changes (add new users/groups)
  const handleSave = async () => {
    if (selectedUsers.length === 0 && selectedGroups.length === 0) {
      return; // Nothing to save
    }

    setIsSaving(true);
    try {
      // Add selected users and groups
      await addAccess(
        token,
        entityType,
        entity.id,
        selectedUsers.map(user => user.id),
        selectedGroups.map(group => group.id)
      );

      // Refresh access list
      await loadAccessList();

      // Clear selections
      setSelectedUsers([]);
      setSelectedGroups([]);

      // Show success message
      setSuccessMessage(t(getTranslationKey('sharing_success')));

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMsg = t(getTranslationKey('error.sharing'));
      setErrorMessage(errorMsg);
      if (onError) onError(errorMsg);
      console.error(`Error sharing ${entityType}:`, err);
    } finally {
      setIsSaving(false);
    }
  };

  // Get dialog title based on entity type
  const getDialogTitle = () => {
    return t(getTranslationKey('title'));
  };

  // Get dialog description based on entity type
  const getDialogDescription = () => {
    return t(getTranslationKey('description'), { name: getEntityName() });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Share2 size={20} />
          {getDialogTitle()}
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getDialogDescription()}
          </Typography>

          {/* User/Group Tabs */}
          <Paper elevation={0} sx={{ mb: 3, borderRadius: '8px', bgcolor: 'background.paper' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="share options"
              variant="fullWidth"
              sx={{ 
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              <Tab
                icon={<UserPlus size={16} />}
                label={t(getTranslationKey('share_with_users'))}
                iconPosition="start"
              />
              <Tab
                icon={<Users size={16} />}
                label={t(getTranslationKey('share_with_groups'))}
                iconPosition="start"
              />
            </Tabs>

            {/* Search field */}
            <Box sx={{ p: 2 }}>
              <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : tabValue === 0
                      ? option.username
                      : option.name
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id} onClick={() => handleSelectItem(option)}>
                    {tabValue === 0 ? option.username : option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tabValue === 0
                      ? t(getTranslationKey('search_users'))
                      : t(getTranslationKey('search_groups'))
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && debouncedSearch(searchTerm)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching ? (
                            <CircularProgress size={20} />
                          ) : (
                            <IconButton size="small" onClick={() => debouncedSearch(searchTerm)}>
                              <Search size={18} />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Paper>

          {/* Selected users */}
          {tabValue === 0 && selectedUsers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t(getTranslationKey('selected_users'))}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedUsers.map(user => (
                  <Chip
                    key={user.id}
                    label={user.username}
                    onDelete={() => handleRemoveUser(user.id)}
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Selected groups */}
          {tabValue === 1 && selectedGroups.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t(getTranslationKey('selected_groups'))}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedGroups.map(group => (
                  <Chip
                    key={group.id}
                    label={group.name}
                    onDelete={() => handleRemoveGroup(group.id)}
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Current access lists */}
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: '8px', 
              bgcolor: 'background.paper',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              overflow: 'hidden'
            }}
          >
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t(getTranslationKey('loading_access'))}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Users with access */}
                {tabValue === 0 && (
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        p: 2, 
                        borderBottom: accessList.users.length > 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'
                      }}
                    >
                      {t(getTranslationKey('users_with_access'))}
                    </Typography>
                    {accessList.users.length > 0 ? (
                      <List disablePadding>
                        {accessList.users.map(access => (
                          <ListItem key={access.id} divider>
                            <ListItemText
                              primary={access.user_details?.username || 'Unknown user'}
                              secondary={access.user_details?.email || ''}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveAccess(access.id)}
                                size="small"
                              >
                                <X size={16} />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t(getTranslationKey('no_users'))}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Groups with access */}
                {tabValue === 1 && (
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        p: 2, 
                        borderBottom: accessList.groups.length > 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'
                      }}
                    >
                      {t(getTranslationKey('groups_with_access'))}
                    </Typography>
                    {accessList.groups.length > 0 ? (
                      <List disablePadding>
                        {accessList.groups.map(access => (
                          <ListItem key={access.id} divider>
                            <ListItemText
                              primary={access.group_details?.name || 'Unknown group'}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveAccess(access.id)}
                                size="small"
                              >
                                <X size={16} />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t(getTranslationKey('no_groups'))}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            disabled={isSaving}
          >
            {t(getTranslationKey('cancel'))}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={(selectedUsers.length === 0 && selectedGroups.length === 0) || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <UserPlus size={16} />}
          >
            {t(getTranslationKey('share'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

ShareDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entity: PropTypes.object.isRequired,
  entityType: PropTypes.oneOf([
    ENTITY_TYPES.SANDBOX,
    ENTITY_TYPES.TEMPLATE,
    ENTITY_TYPES.AI_TUTOR,
    ENTITY_TYPES.GROUP
  ]).isRequired,
  token: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
};

export default ShareDialog;
